/**
 * Parser de correos electronicos en formato .eml (RFC 822 / MIME).
 *
 * Se implementa sin dependencias externas para no agregar librerias al proyecto.
 * El archivo se procesa como cadena binaria (un caracter por byte) de modo que los
 * limites de las partes MIME y las decodificaciones base64 / quoted-printable se
 * resuelvan sobre los bytes originales y no sobre texto ya interpretado.
 */

const TEXT_TYPES = ["text/plain", "text/html"];

/**
 * Convierte un buffer de bytes en cadena binaria (un caracter por byte)
 * @param {Uint8Array} bytes contenido del archivo
 * @returns {string} cadena binaria equivalente
 */
const bytesToBinary = (bytes) => {
  const size = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += size) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + size));
  }
  return binary;
};

/**
 * Convierte una cadena binaria en buffer de bytes
 * @param {string} binary cadena binaria (un caracter por byte)
 * @returns {Uint8Array} buffer equivalente
 */
const binaryToBytes = (binary) => {
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i) & 0xff;
  }
  return bytes;
};

/**
 * Decodifica bytes a texto usando el juego de caracteres declarado en el correo
 * @param {Uint8Array} bytes contenido de la parte
 * @param {string} charset juego de caracteres declarado
 * @returns {string} texto decodificado
 */
const decodeText = (bytes, charset) => {
  const label = String(charset || "utf-8").toLowerCase();
  try {
    return new TextDecoder(label, { fatal: false }).decode(bytes);
  } catch (error) {
    return new TextDecoder("utf-8", { fatal: false }).decode(bytes);
  }
};

/**
 * Decodifica una cadena quoted-printable
 * @param {string} binary contenido codificado
 * @returns {string} cadena binaria decodificada
 */
const decodeQuotedPrintable = (binary) => {
  const withoutSoftBreaks = binary.replace(/=(?:\r\n|\n|\r)/g, "");
  return withoutSoftBreaks.replace(/=([0-9A-Fa-f]{2})/g, (match, hex) =>
    String.fromCharCode(parseInt(hex, 16))
  );
};

/**
 * Decodifica una cadena base64 tolerando saltos de linea y relleno incompleto
 * @param {string} binary contenido codificado
 * @returns {string} cadena binaria decodificada
 */
const decodeBase64 = (binary) => {
  const clean = binary.replace(/[^A-Za-z0-9+/=]/g, "");
  const padded = clean.padEnd(Math.ceil(clean.length / 4) * 4, "=");
  try {
    return atob(padded);
  } catch (error) {
    return "";
  }
};

/**
 * Aplica la decodificacion declarada en Content-Transfer-Encoding
 * @param {string} binary cuerpo de la parte
 * @param {string} encoding valor de la cabecera Content-Transfer-Encoding
 * @returns {Uint8Array} bytes reales de la parte
 */
const decodeTransfer = (binary, encoding) => {
  const value = String(encoding || "").trim().toLowerCase();
  if (value === "base64") {
    return binaryToBytes(decodeBase64(binary));
  }
  if (value === "quoted-printable") {
    return binaryToBytes(decodeQuotedPrintable(binary));
  }
  return binaryToBytes(binary);
};

/**
 * Decodifica las palabras codificadas RFC 2047 de una cabecera
 * @param {string} value valor original de la cabecera
 * @returns {string} valor legible
 */
export const decodeEncodedWords = (value) => {
  const text = String(value || "");
  //Las palabras codificadas contiguas se unen antes de decodificar
  const joined = text.replace(/(=\?[^?]+\?[BbQq]\?[^?]*\?=)\s+(?==\?)/g, "$1");
  return joined.replace(/=\?([^?]+)\?([BbQq])\?([^?]*)\?=/g, (match, charset, kind, payload) => {
    const binary =
      kind.toLowerCase() === "b" ? decodeBase64(payload) : decodeQuotedPrintable(payload.replace(/_/g, " "));
    return decodeText(binaryToBytes(binary), charset);
  });
};

/**
 * Separa el bloque de cabeceras del cuerpo de una parte
 * @param {string} binary contenido completo de la parte
 * @returns {object} objeto con las llaves headerBlock y body
 */
const splitHeaders = (binary) => {
  const match = /\r\n\r\n|\n\n|\r\r/.exec(binary);
  if (!match) {
    return { headerBlock: binary, body: "" };
  }
  return {
    headerBlock: binary.slice(0, match.index),
    body: binary.slice(match.index + match[0].length),
  };
};

/**
 * Convierte un bloque de cabeceras en un mapa de nombre en minusculas a valor
 * @param {string} headerBlock bloque de cabeceras sin decodificar
 * @returns {object} objeto con las llaves map y list
 */
const parseHeaders = (headerBlock) => {
  //Las cabeceras plegadas continuan en lineas que inician con espacio o tabulacion
  const unfolded = headerBlock.replace(/(?:\r\n|\n|\r)[ \t]+/g, " ");
  const map = {};
  const list = [];
  unfolded.split(/\r\n|\n|\r/).forEach((line) => {
    const separator = line.indexOf(":");
    if (separator < 1) {
      return;
    }
    const name = line.slice(0, separator).trim();
    const value = line.slice(separator + 1).trim();
    list.push({ name, value });
    const key = name.toLowerCase();
    map[key] = map[key] === undefined ? value : `${map[key]}, ${value}`;
  });
  return { map, list };
};

/**
 * Decodifica un parametro RFC 2231 con juego de caracteres embebido
 * @param {string} value valor del parametro en formato charset'lang'texto
 * @returns {string} valor legible
 */
const decodeExtendedParam = (value) => {
  const parts = String(value).split("'");
  const charset = parts.length > 2 ? parts[0] : "utf-8";
  const payload = parts.length > 2 ? parts.slice(2).join("'") : String(value);
  const binary = payload.replace(/%([0-9A-Fa-f]{2})/g, (match, hex) =>
    String.fromCharCode(parseInt(hex, 16))
  );
  return decodeText(binaryToBytes(binary), charset);
};

/**
 * Interpreta una cabecera estructurada tipo Content-Type o Content-Disposition
 * @param {string} value valor de la cabecera
 * @returns {object} objeto con las llaves value y params
 */
const parseStructuredHeader = (value) => {
  const text = String(value || "");
  const segments = text.split(";");
  const params = {};
  segments.slice(1).forEach((segment) => {
    const separator = segment.indexOf("=");
    if (separator < 1) {
      return;
    }
    const name = segment.slice(0, separator).trim().toLowerCase();
    const raw = segment.slice(separator + 1).trim().replace(/^"|"$/g, "");
    if (name.endsWith("*")) {
      params[name.slice(0, -1)] = decodeExtendedParam(raw);
      return;
    }
    if (params[name] === undefined) {
      params[name] = raw;
    }
  });
  return { value: segments[0].trim().toLowerCase(), params };
};

/**
 * Busca un delimitador MIME que este al inicio de una linea
 * @param {string} body cuerpo multiparte
 * @param {string} marker delimitador buscado
 * @param {number} from posicion desde donde buscar
 * @returns {number} posicion del delimitador o -1
 */
const indexOfMarker = (body, marker, from) => {
  let index = body.indexOf(marker, from);
  while (index > 0 && body.charAt(index - 1) !== "\n" && body.charAt(index - 1) !== "\r") {
    index = body.indexOf(marker, index + 1);
  }
  return index;
};

/**
 * Divide un cuerpo multiparte en las partes declaradas por su boundary
 * @param {string} body cuerpo de la parte multiparte
 * @param {string} boundary delimitador declarado en el Content-Type
 * @returns {array} arreglo de cadenas binarias, una por parte
 */
const splitMultipart = (body, boundary) => {
  const marker = `--${boundary}`;
  const parts = [];
  let index = indexOfMarker(body, marker, 0);
  while (index !== -1) {
    const afterMarker = index + marker.length;
    if (body.substr(afterMarker, 2) === "--") {
      break;
    }
    const lineBreak = /\r\n|\n|\r/.exec(body.slice(afterMarker));
    if (!lineBreak) {
      break;
    }
    const start = afterMarker + lineBreak.index + lineBreak[0].length;
    const next = indexOfMarker(body, marker, start);
    const end = next === -1 ? body.length : next;
    parts.push(body.slice(start, end).replace(/(?:\r\n|\n|\r)$/, ""));
    if (next === -1) {
      break;
    }
    index = next;
  }
  return parts;
};

/**
 * Determina el nombre del archivo adjunto de una parte
 * @param {object} disposition cabecera Content-Disposition interpretada
 * @param {object} contentType cabecera Content-Type interpretada
 * @returns {string} nombre del adjunto, cadena vacia si no lo declara
 */
const resolveFilename = (disposition, contentType) => {
  const name = disposition.params.filename || contentType.params.name || "";
  return decodeEncodedWords(name);
};

/**
 * Acumula una parte hoja como cuerpo del correo o como adjunto
 * @param {object} part datos de la parte ya decodificada
 * @param {object} result acumulador del correo
 * @returns {void}
 */
const collectLeaf = (part, result) => {
  const { contentType, disposition, filename, bytes, headers } = part;
  const isText = TEXT_TYPES.includes(contentType.value);
  const isAttachment = disposition.value === "attachment" || (!isText && contentType.value !== "");
  if (isText && !isAttachment && filename === "") {
    const text = decodeText(bytes, contentType.params.charset);
    const key = contentType.value === "text/html" ? "html" : "text";
    result[key] = result[key] === null ? text : `${result[key]}\n${text}`;
    return;
  }
  const contentId = String(headers["content-id"] || "").replace(/^<|>$/g, "");
  result.attachments.push({
    filename: filename !== "" ? filename : `adjunto-${result.attachments.length + 1}`,
    contentType: contentType.value !== "" ? contentType.value : "application/octet-stream",
    size: bytes.length,
    bytes,
    contentId,
    inline: disposition.value === "inline" || (contentId !== "" && disposition.value === ""),
  });
};

/**
 * Procesa recursivamente una parte MIME
 * @param {string} binary contenido binario de la parte
 * @param {object} result acumulador del correo
 * @returns {object} cabeceras interpretadas de la parte
 */
const parsePart = (binary, result) => {
  const { headerBlock, body } = splitHeaders(binary);
  const { map, list } = parseHeaders(headerBlock);
  const contentType = parseStructuredHeader(map["content-type"] || "text/plain");
  if (contentType.value.startsWith("multipart/") && contentType.params.boundary) {
    splitMultipart(body, contentType.params.boundary).forEach((child) => parsePart(child, result));
    return { map, list };
  }
  const disposition = parseStructuredHeader(map["content-disposition"] || "");
  collectLeaf(
    {
      contentType,
      disposition,
      filename: resolveFilename(disposition, contentType),
      bytes: decodeTransfer(body, map["content-transfer-encoding"]),
      headers: map,
    },
    result
  );
  return { map, list };
};

/**
 * Parsea un archivo .eml completo
 * @param {ArrayBuffer|Uint8Array} buffer contenido crudo del archivo
 * @returns {object} correo con cabeceras, cuerpos y adjuntos
 */
export const parseEml = (buffer) => {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  const result = { html: null, text: null, attachments: [], headers: {}, headerList: [] };
  const { map, list } = parsePart(bytesToBinary(bytes), result);
  result.headerList = list.map((item) => ({ name: item.name, value: decodeEncodedWords(item.value) }));
  result.headers = {
    subject: decodeEncodedWords(map.subject || ""),
    from: decodeEncodedWords(map.from || ""),
    to: decodeEncodedWords(map.to || ""),
    cc: decodeEncodedWords(map.cc || ""),
    bcc: decodeEncodedWords(map.bcc || ""),
    replyTo: decodeEncodedWords(map["reply-to"] || ""),
    date: decodeEncodedWords(map.date || ""),
    messageId: String(map["message-id"] || "").replace(/^<|>$/g, ""),
  };
  return result;
};

/**
 * Formatea un tamano en bytes a una cadena legible
 * @param {number} size cantidad de bytes
 * @returns {string} tamano con su unidad
 */
export const formatSize = (size) => {
  const units = ["B", "KB", "MB", "GB"];
  let value = Number(size) || 0;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value = value / 1024;
    unit = unit + 1;
  }
  return `${unit === 0 ? value : value.toFixed(1)} ${units[unit]}`;
};
